// The MLS ID service's refusals, in words. A refused write answers 409 with a code in the message; another
// account's id answers 404.
const WORDING: Record<string, string> = {
	// agent-mls-id and client-board
	MLS_ID_REQUIRED: "An MLS ID is required.",
	// agent-mls-id
	ALREADY_ON_PROFILE: "This MLS ID is already on this agent's profile.",
	NOT_ON_FEATURED: "Only an MLS ID linked to Featured Listings can be taken off it.",
	// An agent MLS ID write on a value that isn't aligned, saying which side to fix.
	STRAY_MLS_ID: "Remove this MLS ID from the agent's profile first, in its list that needs review. It's on the profile without its own Featured Listings row.",
	ORPHAN_FEATURED_ROW: "Fix this MLS ID on the Featured Listings page first. It has a Featured Listings row that no agent's MLS ID is using.",
	// client-board
	ALREADY_ADDED: "This MLS ID is already added for this board.",
	// A strict add on an account whose Featured rows come from its agents' MLS IDs.
	USE_AGENT_MLS_ID: "Add this MLS ID from the agent's profile.",
	// A board the account isn't set up for.
	BOARD_NOT_PROVISIONED: "This MLS board isn't set up on your account.",
	// client-board and agent-board DELETE
	LINKED_TO_AGENT: "This MLS ID is linked to an agent. Change it from the agent's profile.",
};

// With the ID and board the user typed and picked, the agent-mls-id refusals name them.
// The code still says which rule refused: nothing here compares ID strings.
const NAMED_WORDING: Record<string, (mlsId: string, boardName: string) => string> = {
	ALREADY_ON_PROFILE: (mlsId, boardName) => `${mlsId} is already on this profile for ${boardName}.`,
};

export interface MlsIdErrorContext {
	mlsId: string;
	boardName: string;
}

const GENERIC = "Something went wrong. Please try again.";

// The code may be in the message (as the service sends it) or in code (the request layer's errorCode, or a thunk's
// rejected value).
export const mlsIdErrorMessage = (error: unknown, context?: MlsIdErrorContext): string => {
	const { message, status, code } = (error ?? {}) as { message?: string; status?: number; code?: string };
	const named = context && context.mlsId && context.boardName ? [code, message].find((value) => value && NAMED_WORDING[value]) : undefined;
	if (named && context) {
		return NAMED_WORDING[named](context.mlsId, context.boardName);
	}
	const known = [code, message].find((value) => value && WORDING[value]);
	if (known) {
		return WORDING[known];
	}
	if (status === 404) {
		return "That MLS ID no longer exists. Reload the page to see the latest.";
	}
	// A 400 or 500 message is the server's own note on a request it couldn't handle, and a 409 with a code this table
	// doesn't word is a rule's code: neither is words for the user.
	if (status === 400 || status === 409 || (status && status >= 500)) {
		return GENERIC;
	}
	return message || GENERIC;
};
